import React, { useCallback, useEffect, useState } from "react";
import { PostContent } from "@/components/modules/Post/PostContent";
import { AboutAuthor } from "@/components/modules/Author/AboutAuthor";
import { PostPageContentProps } from "@/types/modules/post/components";
import styles from "@/styles/app.module.css";
import { RelatedSectionHeadline } from "@/components/modules/Post/RelatedSectionHeadline";
import { RelatedPostItem } from "@/components/modules/Post/RelatedPostItem";
import { PostLayout } from "@/components/layout/PostLayout";
import { RecommendedAuthors } from "@/components/modules/Author/RecommendedAuthors";
import Head from "next/head";
import { useOfflinePostContent } from "@/hooks/indexeddb/useOfflinePostContent";
import usePostByIdProvider from "@/core/dataProviders/providers/posts/usePostByIdProvider";
import { useRouter } from "next/router";
import { useGesture } from "@use-gesture/react";
import { animated, useSpring } from "@react-spring/web";
import { Loader } from "@/components/ui-kit/Loader";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { MainLayout } from "@/components/layout/MainLayout";
import OfflinePageContent from "@/components/layout/OfflinePageContent";
import { DataRequestStateEnum } from "@/core/types/dataRequestsTypes";
import { useIsAppOnline } from "@/hooks/useIsAppOnline";
import { addQueryParamToUrl } from "@/utils/addQueryParamToUrl";
import useTrackPageView from "@/hooks/data-tracking/useTrackPageView";

export const PostPageContent: React.FC<PostPageContentProps> = ({ post: loadedPost, postId, postPreviewId }) => {
  const { getDownloadedPost } = useOfflinePostContent();
  const isMobile = useMediaQuery(561);
  const [post, setPost] = useState(loadedPost);
  const [postLoaded, setPostLoaded] = useState(!!loadedPost);
  const [providedPost, providedPostRequest] = usePostByIdProvider({
    id: postId ? postId.toString() : "",
    postPreviewId: postPreviewId ? postPreviewId.toString() : "",
  });
  const router = useRouter();

  const [loadingArticle, setLoadingArticle] = useState(false);
  const [redirectingToArticle, setRedirectingToArticle] = useState(false);

  const isAppOnline = useIsAppOnline();

  const isRMFoundationFundURL = router.asPath.replace(/\/+$/, "") === "/nadacni-fond-reporter";
  const isRMPeopleURL = router.asPath.replace(/\/+$/, "") === "/lide-v-reporteru";

  const pageType = isRMFoundationFundURL ? "nadační fond reportér" : isRMPeopleURL ? "lidé v reportéru" : "article";
  useTrackPageView(pageType, post);
  const handleSwipe = (x: number, followingPostLink: string) => {
    api.start({
      x: x,
      onResolve: () => {
        setLoadingArticle(true);
        api.set({ opacity: 0 });
        router.push(followingPostLink);
      },
    });
  };

  const fetchDownloadedPost = useCallback(
    async (id: number) => {
      try {
        const res = await getDownloadedPost(id);
        if (res) {
          setPost(res);
        }
      } finally {
        setPostLoaded(true);
      }
    },
    [getDownloadedPost]
  );

  useEffect(() => {
    if (!loadedPost && postId && !isAppOnline) {
      fetchDownloadedPost(postId);
    } else if (!loadedPost) {
      setPostLoaded(true);
    } else if (loadedPost) {
      setPost(loadedPost);
    }
  }, [fetchDownloadedPost, isAppOnline, loadedPost, postId]);

  useEffect(() => {
    if (providedPost) {
      setPost(providedPost);
    }
  }, [providedPost]);

  const [{ x, opacity }, api] = useSpring(() => ({
    x: 0,
    opacity: 1,
  }));

  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    setLoadingArticle(false);
    setRedirectingToArticle(false);
    api.set({ x: 0, opacity: 1 });
  }, [post, api]);

  const bind = useGesture(
    {
      onDrag: ({ down, movement: [mx] }) => {
        api.start({
          x: down ? mx : 0,
          immediate: false,
        });
      },
      onDragEnd: ({ movement: [mx] }) => {
        // if we have 'i' query in route, we are reading issue posts
        if (router.query.i) {
          if (post?.issue_prev && mx > windowWidth / 5) {
            handleSwipe(windowWidth, addQueryParamToUrl(post.issue_prev!, "i", "1"));
          } else if (post?.issue_next && mx < -windowWidth / 5) {
            handleSwipe(-windowWidth, addQueryParamToUrl(post.issue_next!, "i", "1"));
          }
        } else {
          if (post?.prev_link && mx > windowWidth / 5) {
            handleSwipe(windowWidth, post.prev_link!);
          } else if (post?.next_link && mx < -windowWidth / 5) {
            handleSwipe(-windowWidth, post?.next_link!);
          }
        }
      },
    },
    {
      drag: {
        axis: "x",
      },
    }
  );

  return (
    <>
      {post ? (
        <PostLayout post={post}>
          <Head>
            <title>{post?.yoastHeadJson?.title ?? post?.title}</title>
            <link rel="canonical" href={post?.yoastHeadJson?.canonical} />
            {(post?.yoastHeadJson?.description || post?.excerpt) && (
              <meta name="description" content={post?.yoastHeadJson?.description ?? post?.excerpt} key="description" />
            )}
            {(post?.yoastHeadJson?.og_description || post?.excerpt) && (
              <meta
                property="og:description"
                content={post?.yoastHeadJson?.og_description ?? post?.excerpt}
                key="og:description"
              />
            )}
            <meta property="og:title" content={post?.yoastHeadJson?.og_title ?? post?.title} key="og:title" />
            <meta property="og:type" content={post?.yoastHeadJson?.og_type ?? "article"} key="og:type" />
            <meta property="og:url" content={post?.yoastHeadJson?.og_url ?? post?.link} key="og:url" />
            {post?.date && !isNaN(Date.parse(post.date)) && (
              <meta property="og:updated_time" content={new Date(post.date).toISOString()} key="og:updated_time" />
            )}
            <meta property="og:locale" content={post?.yoastHeadJson?.og_locale ?? "cs_CZ"} key="og:locale" />
            {post?.thumbnail.full.url && (
              <meta property="og:image" content={post?.thumbnail?.full?.url} key="og:image" />
            )}
            {post?.yoastHeadJson?.schema && (
              <script
                key="head-json-ld"
                id="meta-schema"
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(post.yoastHeadJson.schema) }}
              ></script>
            )}
          </Head>
          {/*<SwipeDialog />*/}
          {(loadingArticle ||
            redirectingToArticle ||
            providedPostRequest === DataRequestStateEnum.PENDING ||
            providedPostRequest === DataRequestStateEnum.RUNNING ||
            (providedPost && providedPost?.id !== post?.id)) &&
            isAppOnline && (
              <div className="fixed inset-0 z-10 flex min-h-screen items-center justify-center bg-white text-orange dark:bg-dark">
                <div className="min-h-screen" />
                {loadingArticle && <Loader />}
              </div>
            )}

          <animated.div
            className="touch-pan-y pb-2 transition-opacity"
            {...(isMobile ? bind() : {})}
            style={{
              x,
              opacity,
            }}
          >
            <div className="pb-6">
              {!postLoaded ? (
                <div className="flex items-center justify-center py-10 text-orange">
                  <Loader />
                </div>
              ) : (
                <PostContent post={post} />
              )}
            </div>
            <div className={styles.contentContainer}>
              {!post?.isAdvertorial && <AboutAuthor author={post?.authors[0]!} />}

              {post?.relatedArticles?.map((section) => (
                <div className="mt-8" key={section.title}>
                  {section.category ? (
                    <RelatedSectionHeadline ctaBasicLink={section.category?.link} title={section.title} />
                  ) : section.topic ? (
                    <RelatedSectionHeadline ctaBasicLink={section.topic?.link} title={section.title} />
                  ) : (
                    <RelatedSectionHeadline title={section.title} />
                  )}

                  {section.articles?.map((post) => (
                    <RelatedPostItem
                      post={post}
                      key={`article-sections-post-${post.id}`}
                      onClick={() => setRedirectingToArticle(true)}
                    />
                  ))}

                  {section.authors?.map((author) => (
                    <RecommendedAuthors author={author} key={`article-sections-author-${author.id}`} />
                  ))}
                </div>
              ))}
            </div>
          </animated.div>
        </PostLayout>
      ) : (
        <MainLayout>{!isAppOnline && postLoaded && <OfflinePageContent />}</MainLayout>
      )}
    </>
  );
};
